import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { map, Observable, switchMap } from 'rxjs';
import { APP_CONFIG } from '../config/app-config.token';
import { ActiveOrganizationStore } from '../entitlements/active-organization.store';

export interface AuthHttpOptions {
  params?: HttpParams | Record<string, string | number | boolean>;
  headers?: HttpHeaders;
}

@Injectable({ providedIn: 'root' })
export class AuthHttpClient {
  private readonly http = inject(HttpClient);
  private readonly msal = inject(MsalService);
  private readonly scopes = inject(APP_CONFIG).auth.msal.apiScopes;
  private readonly activeOrganization = inject(ActiveOrganizationStore);

  get<T>(url: string, options?: AuthHttpOptions): Observable<T> {
    return this.withAuthHeaders(options?.headers).pipe(
      switchMap((headers) => this.http.get<T>(url, { params: options?.params, headers })),
    );
  }

  /** GET binario con headers de respuesta (p. ej. Content-Disposition para descargas). */
  getBlob(url: string, options?: AuthHttpOptions): Observable<HttpResponse<Blob>> {
    return this.withAuthHeaders(options?.headers).pipe(
      switchMap((headers) =>
        this.http.get(url, {
          params: options?.params,
          headers,
          responseType: 'blob',
          observe: 'response',
        }),
      ),
    );
  }

  post<T>(url: string, body: unknown, options?: AuthHttpOptions): Observable<T> {
    return this.withAuthHeaders(options?.headers).pipe(
      switchMap((headers) => this.http.post<T>(url, body, { params: options?.params, headers })),
    );
  }

  put<T>(url: string, body: unknown, options?: AuthHttpOptions): Observable<T> {
    return this.withAuthHeaders(options?.headers).pipe(
      switchMap((headers) => this.http.put<T>(url, body, { params: options?.params, headers })),
    );
  }

  patch<T>(url: string, body: unknown, options?: AuthHttpOptions): Observable<T> {
    return this.withAuthHeaders(options?.headers).pipe(
      switchMap((headers) => this.http.patch<T>(url, body, { params: options?.params, headers })),
    );
  }

  delete<T>(url: string, options?: AuthHttpOptions): Observable<T> {
    return this.withAuthHeaders(options?.headers).pipe(
      switchMap((headers) => this.http.delete<T>(url, { params: options?.params, headers })),
    );
  }

  private withAuthHeaders(headers?: HttpHeaders): Observable<HttpHeaders> {
    const account = this.msal.instance.getActiveAccount() ?? this.msal.instance.getAllAccounts()[0];

    return this.msal.acquireTokenSilent({ scopes: this.scopes, account }).pipe(
      map(({ accessToken }) => {
        const withAuth = (headers ?? new HttpHeaders()).set(
          'Authorization',
          `Bearer ${accessToken}`,
        );
        const organizationId = this.activeOrganization.activeId();

        return organizationId ? withAuth.set('X-Organization-Id', organizationId) : withAuth;
      }),
    );
  }
}
